import { Text } from "@/components/Text";
import { Card, Skeleton } from "@/components/ui";
import { useFaucetActivity, useFaucetStatus } from "@/hooks/api/useFaucet";
import { CircleSmallIcon } from "lucide-react";
import { useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { TransactionInfo } from "./Activity/TransactionInfo";

export function RecentActivity() {
  const { data: activity, isError, isLoading, error } = useFaucetActivity();
  const { data: status } = useFaucetStatus();

  const previousTxHashes = useRef<Set<string>>(new Set());
  const newTxHashes = useRef<Set<string>>(new Set());

  // Track if this is the first load
  const isFirstLoad = useRef(true);

  const transactions = useMemo(() => {
    // Ensure we have valid dates for all transactions
    const validatedDrips =
      activity?.drips.map((drip) => {
        if (!drip.date) {
          // Set to current time if missing
          return { ...drip, date: new Date().toISOString() };
        }
        return drip;
      }) ?? [];

    // Get latest 10 transactions, but ensure proper sorting by date in descending order
    const sortedTransactions = [...validatedDrips]
      .sort((a, b) => {
        // Get timestamps, fallback to 0 if invalid
        const dateA = new Date(a.date).getTime() || 0;
        const dateB = new Date(b.date).getTime() || 0;
        return dateB - dateA; // Most recent first
      })
      .slice(0, 10);

    // Process transactions with permanent "new" status
    const updatedTransactions = sortedTransactions.map((tx) => {
      // Check if transaction was already seen in any previous fetch
      if (!previousTxHashes.current.has(tx.tx)) {
        // This is a newly discovered transaction
        if (!isFirstLoad.current) {
          // Not first load, so we should mark it as new and remember it
          newTxHashes.current.add(tx.tx);
        }
      }

      // A transaction is "new" if it's in our newTxHashes set
      const isNew = newTxHashes.current.has(tx.tx);
      return { ...tx, isNew };
    });

    // Update our reference set with all transaction hashes (to detect what's new in future fetches)
    previousTxHashes.current = new Set(sortedTransactions.map((tx) => tx.tx));

    // Update first load flag
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
    }

    // Set initial transactions
    return updatedTransactions;
  }, [activity]);

  if (isLoading) {
    return (
      <Card className="gap-2">
        <Text variant="heading" className="mb-4">
          Recent Activity
        </Text>
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} isLoading={true} className="h-18" />
        ))}
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <Text variant="heading">Recent Activity</Text>
        <Card variant="secondary">
          {error.message ?? "Unable to display Recent activities."}
        </Card>
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex gap-2 justify-between">
        <Text variant="heading">Recent Activity</Text>

        <div className="flex gap-1 items-center justify-center mr-2">
          <CircleSmallIcon
            data-active={status?.active}
            stroke="0"
            className="h-5 w-5 data-[active=true]:fill-green-500 data-[active=false]:fill-gray-400"
          />
          <Text>{status?.active ? "Live" : "Paused"}</Text>
        </div>
      </div>

      {transactions.length === 0 ? (
        <Text variant="muted">No recent activity.</Text>
      ) : (
        <div className="grid gap-3">
          <AnimatePresence>
            {transactions.map((tx) => (
              <motion.div
                key={tx.tx}
                initial={
                  tx.isNew
                    ? {
                        opacity: 0,
                        y: -20,
                        backgroundColor: "var(--purple-8)",
                        borderRadius: 16,
                      }
                    : { opacity: 1 }
                }
                animate={{
                  opacity: 1,
                  y: 0,
                  // TODO: Change this
                  backgroundColor: "rgba(74, 222, 128, 0)",
                }}
                transition={{
                  duration: 0.5,
                  backgroundColor: { duration: 1.5 },
                }}
              >
                <TransactionInfo
                  txHash={tx.tx}
                  amount={tx.amount}
                  timestamp={tx.date}
                  tokenSymbol={tx.tokenSymbol}
                  isNew={tx.isNew}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </Card>
  );
}
